import type { CSSProperties } from 'react'
import { theme } from '../theme'

const sectionTitle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  color: theme.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  margin: '0 0 16px',
}

const card: CSSProperties = {
  backgroundColor: theme.surface,
  border: `1px solid ${theme.border}`,
  borderRadius: '10px',
  padding: '20px 24px',
  marginBottom: '20px',
}

const stepNum: CSSProperties = {
  flexShrink: 0,
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  backgroundColor: theme.accentSubtle,
  border: `1px solid ${theme.accent}`,
  color: theme.accent,
  fontSize: '11px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const code = (text: string) => (
  <code
    style={{
      fontFamily: 'ui-monospace, monospace',
      fontSize: '11px',
      color: theme.accent,
      backgroundColor: theme.accentSubtle,
      padding: '1px 5px',
      borderRadius: '3px',
    }}
  >
    {text}
  </code>
)

export const ReinitPage = () => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
      <span
        style={{
          fontSize: '10px',
          fontWeight: 700,
          color: theme.accent,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          backgroundColor: theme.accentSubtle,
          padding: '3px 8px',
          borderRadius: '4px',
        }}
      >
        Init lifecycle
      </span>
    </div>

    <h1
      style={{
        fontSize: '24px',
        fontWeight: 700,
        color: theme.text,
        margin: '0 0 6px',
        letterSpacing: '-0.02em',
      }}
    >
      🔁 Install-once init
    </h1>
    <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 28px' }}>
      {code('ChiliWebData.init()')} is install-once. Calling it twice is a no-op and logs a warning.
      To switch tenant or reload configuration,{' '}
      <strong style={{ color: theme.text }}>reload the page</strong> — this is the only flow
      customers and this test harness support.
    </p>

    {/* What init does — and doesn't */}
    <div style={card}>
      <p style={sectionTitle}>Behavior</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {[
          'First init call installs the deanon iframe, wires up listeners, and initializes Jitsu.',
          'Second init call with any params logs a warning and returns — no state is touched.',
          'The Initialize button disables itself once init succeeds (no re-click).',
          'If the tenant fields change after a successful init, an amber banner appears telling you to reload.',
          'Reloading the page re-fetches scripts (including the tracking script from the new tenant-subdomain URL, which may resolve to a different cluster like canary vs prod) and resets all in-memory state.',
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <span
              style={{
                fontSize: '11px',
                color: theme.accent,
                flexShrink: 0,
                marginTop: '1px',
              }}
            >
              ✓
            </span>
            <span style={{ fontSize: '13px', color: theme.textMuted }}>{item}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Manual verification steps */}
    <div style={card}>
      <p style={sectionTitle}>Manual test steps</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {[
          {
            step: '1',
            text: (
              <>
                Enter <strong style={{ color: theme.text }}>Tenant A</strong> credentials and click{' '}
                <strong style={{ color: theme.text }}>🔥 Initialize</strong>. Verify the success
                badge appears and the button becomes disabled.
              </>
            ),
          },
          {
            step: '2',
            text: (
              <>
                In the DevTools console, run{' '}
                {code(
                  "window.ChiliWebData.init({ tenantSubdomain: 'chilichat', tenantId: 'chilichat.app' })"
                )}{' '}
                (or any params). Verify a warning is logged — something like{' '}
                {code('init called twice — ignoring')}. Confirm{' '}
                {code("document.querySelectorAll('iframe.chilipiper-web-data-frame').length")} still
                reports {code('1')} — no extra iframe, no state change.
              </>
            ),
          },
          {
            step: '3',
            text: (
              <>
                Change the <strong style={{ color: theme.text }}>Tenant Subdomain</strong> input to{' '}
                <strong style={{ color: theme.text }}>Tenant B</strong>. Verify the amber{' '}
                <strong style={{ color: theme.text }}>
                  "Tenant changed — reload the page to re-initialize…"
                </strong>{' '}
                banner appears and the Initialize button stays disabled. Revert the input to its
                original value — the banner disappears.
              </>
            ),
          },
          {
            step: '4',
            text: (
              <>
                Set the tenant to <strong style={{ color: theme.text }}>Tenant B</strong> again,
                then hit the browser reload (Cmd+R / F5). After reload: the URL query string still
                carries Tenant B, the banner is gone, and the Initialize button is enabled again.
                Click Initialize — it succeeds with Tenant B's configuration.
              </>
            ),
          },
          {
            step: '5',
            text: (
              <>
                In production, the reload also re-fetches the tracking script from{' '}
                {code('{tenantSubdomain}.cpcdp.{tld}/web-data/tracking/index.js')}. A tenant in
                canary and a tenant in prod are served by different clusters, so the bundle can
                actually differ — which is why switching tenants must go through a full reload, not
                a programmatic re-init.
              </>
            ),
          },
        ].map(({ step, text }) => (
          <div key={step} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div style={stepNum}>{step}</div>
            <p style={{ margin: 0, fontSize: '13px', color: theme.textMuted, lineHeight: '1.6' }}>
              {text}
            </p>
          </div>
        ))}
      </div>
    </div>
  </div>
)
